import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { Area, AreaChart, CartesianGrid, Cell, Pie, PieChart, ResponsiveContainer, Tooltip as RechartsTooltip, XAxis, YAxis } from 'recharts'
import AppDropdown from '../../../components/AppDropdown'
import AppPagination from '../../../components/AppPagination'
import { AppTooltip, ChartTooltipSurface } from '../../../components/AppTooltip'
import useBodyScrollLock from '../../../hooks/useBodyScrollLock'
import { resolveMediaUrl } from '../../../utils/mediaUrl'
import { useTaskAnalytics } from './hooks/useTaskAnalytics'
import './TaskAnalyticsScreen.css'

function formatPercent(value) {
  return `${Number(value || 0).toFixed(1)}%`
}

function formatPoints(value) {
  const parsed = Number(value || 0)
  if (parsed >= 1000) return `${(parsed / 1000).toFixed(1)}k`
  return String(Math.round(parsed))
}

function initials(name) {
  const chunks = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!chunks.length) return 'U'
  return chunks
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('')
}

function ringDashOffset(percent) {
  const normalized = Math.max(0, Math.min(100, Number(percent || 0)))
  const circumference = 2 * Math.PI * 80
  return circumference - (normalized / 100) * circumference
}

function titleCase(text) {
  return String(text || '')
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (c) => c.toUpperCase())
    .trim()
}

const POINT_COLORS = {
  HIGH: 'var(--primary)',
  MEDIUM: 'var(--secondary)',
  LOW: 'var(--secondary-container)',
}

function AnalyticsTaskTable({ columns, rows, rowKey, renderRow, emptyMessage = 'No records match the current filters.', className = '' }) {
  return (
    <div className={`analytics-task-list-card ${className}`}>
      <div className="analytics-task-list-scroll">
        <table className="analytics-task-list-table">
          <thead>
            <tr>
              {columns.map((column) => <th key={column}>{column}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.length ? rows.map((row, index) => <tr key={rowKey(row, index)}>{renderRow(row, index)}</tr>) : (
              <tr><td className="analytics-task-list-empty" colSpan={columns.length}>{emptyMessage}</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function TaskDelayTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null
  const item = payload[0]?.payload || {}
  return (
    <ChartTooltipSurface className="analytics-task-tooltip">
      <p className="analytics-task-tooltip__title">{label} ({item.periodStart || ''} to {item.periodEnd || ''})</p>
      <div className="analytics-task-tooltip__row">
        <span className="analytics-task-tooltip__series">
          <span className="analytics-task-tooltip__dot analytics-task-tooltip__dot--delay" />
          Delayed Tasks
        </span>
        <strong>{Number(item.overdueCount || 0)}</strong>
      </div>
      <div className="analytics-task-tooltip__row">
        <span>Avg Delay</span>
        <strong>{item.avgDelayDays ? `${item.avgDelayDays}d` : '0d'}</strong>
      </div>
      <div className="analytics-task-tooltip__row">
        <span>Most Affected</span>
        <strong>{item.mostAffectedProject || '-'}</strong>
      </div>
    </ChartTooltipSurface>
  )
}

export default function TaskAnalyticsScreen() {
  const navigate = useNavigate()
  const { state, actions } = useTaskAnalytics()
  useBodyScrollLock(state.isTeamOpen || state.isDetailsOpen)

  const insights = state.insights || {}
  const onTime = insights.onTimeCompletion || {}
  const delayTrend = Array.isArray(insights.delayTrend) ? insights.delayTrend : []
  const taskLoad = insights.taskLoadPerUser || {}
  const contributors = Array.isArray(taskLoad.contributors) ? taskLoad.contributors : []
  const points = insights.pointsDistribution || {}
  const pointSeries = Array.isArray(points.series) ? points.series : []
  const riskSummary = insights.highSlaRiskSummary || {}
  const urgentEscalations = Number(state.riskCounts?.urgentEscalations ?? riskSummary?.urgentEscalations ?? 0)
  const riskRows = Array.isArray(state.riskRows) ? state.riskRows : []
  const riskTotalPages = Math.max(1, Math.ceil(Number(state.riskTotal || 0) / Number(state.riskPageSize || 5)))
  const onTimeDelta = Number(onTime.deltaPercent || 0)
  const onTimeTrendIcon = onTimeDelta >= 0 ? 'trending_up' : 'trending_down'
  const onTimeTrendTone = onTimeDelta >= 0 ? 'text-emerald-600' : 'text-error'

  const delaySeries = useMemo(
    () =>
      delayTrend.map((row) => ({
        label: row.label,
        periodStart: row.periodStart,
        periodEnd: row.periodEnd,
        overdueCount: Number(row.overdueCount || 0),
        avgDelayDays: row.avgDelayDays,
        mostAffectedProject: row.mostAffectedProject,
      })),
    [delayTrend],
  )

  const pieRows = useMemo(
    () =>
      pointSeries.map((row) => ({
        label: row.label,
        value: Number(row.points || 0),
        percent: Number(row.percent || 0),
        taskCount: Number(row.taskCount || 0),
        description: row.description,
      })),
    [pointSeries],
  )

  const drillColumns = Array.isArray(state.drilldown?.columns) ? state.drilldown.columns : []
  const drillRows = Array.isArray(state.drilldown?.rows) ? state.drilldown.rows : []

  return (
    <div className="w-full max-w-[1600px] px-8 py-8">
      <section className="mb-10 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <span className="mb-2 block text-[10px] font-extrabold uppercase tracking-[0.2em] text-primary">Performance Overview</span>
          <h2 className="mb-4 text-4xl font-extrabold leading-none tracking-tight text-on-surface lg:text-5xl">Task Analytics</h2>
          <p className="max-w-md text-on-surface-variant">
            Real-time health telemetry across enterprise task flows, delivery SLAs, and contributor velocity.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-outline-variant/20 bg-surface-container-low p-2">
          <div className="flex flex-col px-3">
            <label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">Date Range</label>
            <AppDropdown
              value={state.presetDays}
              onChange={(value) => actions.applyPreset(String(value))}
              options={state.presetOptions}
              placeholder="Last 30 Days"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none"
              menuClassName="min-w-[180px]"
            />
          </div>
          <div className="flex flex-col px-3"><label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">User</label><AppDropdown value={state.userId} onChange={(value) => actions.setUserId(String(value))} options={state.userOptions} placeholder="All Users" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none" menuClassName="min-w-[190px]" /></div>
          <div className="flex flex-col px-3"><label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">Task Status</label><AppDropdown value={state.status} onChange={(value) => actions.setStatus(String(value))} options={state.statusOptions} placeholder="All Statuses" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none" menuClassName="min-w-[160px]" /></div>
          <div className="flex flex-col px-3"><label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">Priority</label><AppDropdown value={state.priority} onChange={(value) => actions.setPriority(String(value))} options={state.priorityOptions} placeholder="All Priorities" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none" menuClassName="min-w-[150px]" /></div>
          <div className="flex flex-col px-3"><label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">SLA Status</label><AppDropdown value={state.slaStatus} onChange={(value) => actions.setSlaStatus(String(value))} options={state.slaStatusOptions} placeholder="All SLA States" triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none" menuClassName="min-w-[170px]" /></div>
          <div className="h-8 w-px bg-outline-variant/30" />
          <div className="flex flex-col px-3">
            <label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">Department</label>
            <AppDropdown
              value={state.departmentId}
              onChange={(value) => actions.setDepartmentId(String(value))}
              options={state.departmentOptions}
              placeholder="All Departments"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none"
              menuClassName="min-w-[200px]"
            />
          </div>
          <div className="h-8 w-px bg-outline-variant/30" />
          <div className="flex flex-col px-3">
            <label className="mb-1 text-[9px] font-bold uppercase tracking-wider text-outline">Project</label>
            <AppDropdown
              value={state.projectId}
              onChange={(value) => actions.setProjectId(String(value))}
              options={state.projectOptions}
              placeholder="All Projects"
              triggerClassName="h-auto min-h-0 rounded-none border-none bg-transparent px-0 py-0 text-sm font-semibold text-on-surface shadow-none"
              menuClassName="min-w-[220px]"
            />
          </div>
          <button
            type="button"
            className="ml-2 flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-white transition-opacity hover:opacity-90"
            aria-label="Reset filters"
            onClick={actions.resetFilters}
          >
            <span className="material-symbols-outlined">filter_list</span>
          </button>
        </div>
      </section>

      {state.error ? <div className="analytics-task-status analytics-task-status-error mb-4">{state.error}</div> : null}
      {state.isLoading ? <div className="analytics-task-status mb-4">Loading task analytics...</div> : null}

      <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
        {/* On-Time Completion */}
        <div className="relative flex flex-col items-center justify-between rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 md:col-span-4">
          <div className="w-full">
            <h3 className="text-xs font-bold uppercase tracking-widest text-outline">On-Time Completion</h3>
            <p className="mt-1 text-xs text-on-surface-variant">
              On-Time: <span className="font-bold text-on-surface">{Number(onTime.onTimeCount || 0)}</span> of{' '}
              <span className="font-bold text-on-surface">{Number(onTime.totalActiveCount || 0)}</span> active/completed tasks ={' '}
              <span className="font-extrabold text-primary">{Math.round(Number(onTime.currentPercent || 0))}%</span>
            </p>
          </div>

          <div
            role="button"
            tabIndex={0}
            onClick={() => actions.openTasks()}
            onKeyDown={(e) => e.key === 'Enter' && actions.openTasks()}
            className="group relative my-4 h-44 w-44 cursor-pointer transition-transform hover:scale-105"
            title="Click to view all task deliverables"
          >
            <svg className="h-full w-full -rotate-90">
              <circle cx="88" cy="88" r="74" className="text-surface-container-high" fill="transparent" stroke="currentColor" strokeWidth="12" />
              <circle
                cx="88"
                cy="88"
                r="74"
                className="text-primary"
                fill="transparent"
                stroke="currentColor"
                strokeWidth="12"
                strokeDasharray={2 * Math.PI * 74}
                strokeDashoffset={ringDashOffset(onTime.currentPercent)}
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-extrabold text-on-surface">{Math.round(Number(onTime.currentPercent || 0))}%</span>
              <AppTooltip content="Compared with the immediately previous equal-length date range.">
                <span className={`flex items-center text-[10px] font-bold ${onTimeTrendTone}`}>
                  <span className="material-symbols-outlined text-sm">{onTimeTrendIcon}</span>
                  {onTimeDelta >= 0 ? '+' : ''}
                  {formatPercent(onTimeDelta)}
                </span>
              </AppTooltip>
              <span className="text-[9px] font-bold uppercase text-primary group-hover:underline mt-1">View Tasks</span>
            </div>
          </div>

          {/* Clickable counts for On Time, Delayed, Pending, Overdue */}
          <div className="grid w-full grid-cols-4 gap-2 border-t border-outline-variant/10 pt-4 text-center">
            <button
              type="button"
              onClick={() => actions.openTasks(null, null, 'ON_TIME')}
              className="flex flex-col items-center rounded p-1 transition-colors hover:bg-surface-container-low"
            >
              <span className="text-[9px] font-bold uppercase text-outline">On Time</span>
              <span className="text-base font-extrabold text-emerald-600">{Number(onTime.completedOnTimeCount || onTime.onTimeCount || 0)}</span>
            </button>
            <button
              type="button"
              onClick={() => actions.openTasks(null, null, 'DELAYED')}
              className="flex flex-col items-center rounded p-1 transition-colors hover:bg-surface-container-low"
            >
              <span className="text-[9px] font-bold uppercase text-outline">Delayed</span>
              <span className="text-base font-extrabold text-amber-600">{Number(onTime.completedLateCount || onTime.delayedCount || 0)}</span>
            </button>
            <button
              type="button"
              onClick={() => actions.openTasks(null, null, 'PENDING')}
              className="flex flex-col items-center rounded p-1 transition-colors hover:bg-surface-container-low"
            >
              <span className="text-[9px] font-bold uppercase text-outline">Pending</span>
              <span className="text-base font-extrabold text-blue-600">{Number(onTime.pendingCount || 0)}</span>
            </button>
            <button
              type="button"
              onClick={() => actions.openTasks(null, null, 'OVERDUE')}
              className="flex flex-col items-center rounded p-1 transition-colors hover:bg-surface-container-low"
            >
              <span className="text-[9px] font-bold uppercase text-outline">Overdue</span>
              <span className="text-base font-extrabold text-error">{Number(onTime.overdueCount || 0)}</span>
            </button>
          </div>
        </div>

        {/* Delay Trend */}
        <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 md:col-span-8">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-outline">Delay Trend</h3>
              <p className="text-xs text-on-surface-variant">Delayed tasks & average resolution lag (click to drill down)</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 rounded-lg border border-outline-variant/20 bg-surface-container-low p-0.5">
                <button
                  type="button"
                  onClick={() => actions.setDelayTrendView('chart')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.delayTrendView === 'chart' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">show_chart</span>
                  Chart
                </button>
                <button
                  type="button"
                  onClick={() => actions.setDelayTrendView('table')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.delayTrendView === 'table' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">table_chart</span>
                  Table
                </button>
              </div>
              <span className="flex items-center text-[10px] font-bold text-outline">
                <span className="mr-1 h-2 w-2 rounded-full bg-primary" /> Overdue Tasks
              </span>
            </div>
          </div>

          {state.delayTrendView === 'table' ? (
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="p-2.5">Period</th>
                    <th className="p-2.5 text-right">Delayed Tasks</th>
                    <th className="p-2.5 text-right">Avg Delay</th>
                    <th className="p-2.5">Most Affected Project</th>
                    <th className="p-2.5 text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {delaySeries.map((row) => (
                    <tr key={row.label} className="border-b border-outline-variant/10 hover:bg-surface-container-low/50">
                      <td className="p-2.5 font-bold text-on-surface">
                        {row.label} <span className="text-[10px] font-normal text-on-surface-variant">({row.periodStart} to {row.periodEnd})</span>
                      </td>
                      <td className="p-2.5 text-right font-mono font-bold text-error">{row.overdueCount}</td>
                      <td className="p-2.5 text-right font-mono">{row.avgDelayDays ? `${row.avgDelayDays}d` : '0d'}</td>
                      <td className="p-2.5 text-on-surface-variant">{row.mostAffectedProject || '-'}</td>
                      <td className="p-2.5 text-center">
                        <button
                          type="button"
                          className="text-xs font-bold text-primary hover:underline"
                          onClick={() => actions.openDelayTrend(row.periodStart, row.periodEnd)}
                        >
                          Drill Down
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="h-52 w-full px-2">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={delaySeries}
                  margin={{ top: 8, right: 12, left: 12, bottom: 8 }}
                  className="cursor-pointer"
                  onClick={(data) => {
                    if (data?.activePayload?.[0]?.payload) {
                      const p = data.activePayload[0].payload
                      actions.openDelayTrend(p.periodStart, p.periodEnd)
                    }
                  }}
                >
                  <defs>
                    <linearGradient id="taskDelayArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="var(--primary-container)" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="var(--primary-container)" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" stroke="rgba(67, 70, 83, 0.14)" />
                  <XAxis dataKey="label" axisLine={false} tickLine={false} tickMargin={8} minTickGap={18} interval="preserveStartEnd" />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    width={40}
                    stroke="rgba(67, 70, 83, 0.6)"
                    fontSize={11}
                  />
                  <RechartsTooltip cursor={false} content={<TaskDelayTooltip />} />
                  <Area
                    type="natural"
                    dataKey="overdueCount"
                    stroke="var(--primary-container)"
                    fill="url(#taskDelayArea)"
                    strokeWidth={3}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>

        {/* Task Load per User */}
        <div className="rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 md:col-span-7">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <h3 className="text-xs font-bold uppercase tracking-widest text-outline">Task Load per User</h3>
              <p className="text-xs text-on-surface-variant">Capacity load split (click user to open assigned tasks)</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1 rounded-lg border border-outline-variant/20 bg-surface-container-low p-0.5">
                <button
                  type="button"
                  onClick={() => actions.setTaskLoadView('chart')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.taskLoadView === 'chart' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">view_agenda</span>
                  Bars
                </button>
                <button
                  type="button"
                  onClick={() => actions.setTaskLoadView('table')}
                  className={`flex items-center gap-1 rounded px-2.5 py-1 text-xs font-semibold transition-all ${state.taskLoadView === 'table' ? 'bg-primary text-white shadow-sm' : 'text-on-surface-variant hover:text-primary'}`}
                >
                  <span className="material-symbols-outlined text-[14px]">table_chart</span>
                  Table
                </button>
              </div>
              <button
                type="button"
                className="flex items-center text-xs font-bold text-primary hover:underline"
                onClick={() => actions.openUsers()}
              >
                View Team <span className="material-symbols-outlined ml-0.5 text-xs">chevron_right</span>
              </button>
            </div>
          </div>

          {state.taskLoadView === 'table' ? (
            <div className="max-h-56 overflow-y-auto">
              <table className="w-full border-collapse text-left text-xs">
                <thead>
                  <tr className="border-b border-outline-variant/20 bg-surface-container-low text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">
                    <th className="p-2">User</th>
                    <th className="p-2 text-right">Total</th>
                    <th className="p-2 text-right">Open</th>
                    <th className="p-2 text-right">In Prog</th>
                    <th className="p-2 text-right">Overdue</th>
                    <th className="p-2 text-right">Done</th>
                    <th className="p-2 text-right">Load %</th>
                  </tr>
                </thead>
                <tbody>
                  {contributors.map((item) => (
                    <tr
                      key={`load-tbl-${item.userId}`}
                      onClick={() => actions.openUsers(null, null, item.userId)}
                      className="cursor-pointer border-b border-outline-variant/10 hover:bg-surface-container-low/60"
                    >
                      <td className="p-2 font-bold text-on-surface flex items-center gap-1.5">
                        {item.name}
                      </td>
                      <td className="p-2 text-right font-mono font-bold">{item.taskCount}</td>
                      <td className="p-2 text-right font-mono text-blue-600">{item.openCount || 0}</td>
                      <td className="p-2 text-right font-mono text-amber-600">{item.inProgressCount || 0}</td>
                      <td className="p-2 text-right font-mono font-bold text-error">{item.overdueCount || 0}</td>
                      <td className="p-2 text-right font-mono text-emerald-600">{item.completedCount || 0}</td>
                      <td className="p-2 text-right font-mono font-bold text-primary">{Math.round(item.loadPercent || 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="space-y-4">
              {contributors.slice(0, 5).map((item) => {
                const totalTasks = Math.max(0, Number(item.taskCount || 0))
                const inProgress = Math.max(0, Number(item.inProgressCount || 0))
                const progressPercent = totalTasks > 0 ? Math.min(100, (inProgress / totalTasks) * 100) : 0

                return (
                  <div
                    key={`load-${item.userId || item.name}`}
                    onClick={() => actions.openUsers(null, null, item.userId)}
                    className="cursor-pointer rounded-lg p-1.5 transition-colors hover:bg-surface-container-low/50"
                  >
                    <div className="mb-1.5 flex justify-between text-xs font-bold">
                      <span className="flex items-center gap-2">
                        {item.profilePhotoPath ? (
                          <img src={resolveMediaUrl(item.profilePhotoPath)} alt={item.name} className="h-6 w-6 rounded-full object-cover" />
                        ) : (
                          <span className="flex h-6 w-6 items-center justify-center rounded-full bg-slate-200 text-[10px] font-bold">{initials(item.name)}</span>
                        )}
                        <span className="text-on-surface">{item.name}</span>
                      </span>
                      <span className="text-on-surface-variant font-mono">
                        {totalTasks} Tasks <span className="text-[10px] text-primary">({Math.round(item.loadPercent || 0)}% load)</span>
                      </span>
                    </div>
                    <AppTooltip content={`Total: ${totalTasks} | Open: ${item.openCount || 0} | In Progress: ${inProgress} | Overdue: ${Number(item.overdueCount || 0)} | Completed: ${item.completedCount || 0}`}>
                      <div className="h-2 w-full overflow-hidden rounded-full bg-surface-variant/80">
                        <div
                          className={`h-full rounded-full ${Number(item.overdueCount || 0) > 0 ? 'bg-error' : 'bg-primary'}`}
                          style={{ width: `${Math.max(inProgress > 0 ? 6 : 0, progressPercent)}%` }}
                        />
                      </div>
                    </AppTooltip>
                  </div>
                )
              })}
              {!contributors.length ? <p className="text-sm text-on-surface-variant">No assignee load data available.</p> : null}
            </div>
          )}
        </div>

        {/* Points Distribution */}
        <div className="flex flex-col rounded-xl border border-outline-variant/20 bg-surface-container-lowest p-6 md:col-span-5">
          <div className="mb-2">
            <h3 className="text-xs font-bold uppercase tracking-widest text-outline">Points Distribution</h3>
            <p className="mt-1 text-[11px] text-on-surface-variant">
              <span className="font-bold text-primary">Rule:</span> High (3x), Medium (2x), Low (1x) on deliverables
            </p>
          </div>
          <div className="flex flex-1 items-center justify-between gap-6">
            <div className="h-36 w-36">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={pieRows} dataKey="value" nameKey="label" innerRadius={44} outerRadius={60} paddingAngle={2}>
                    {pieRows.map((entry) => (
                      <Cell key={`cell-${entry.label}`} fill={POINT_COLORS[entry.label] || 'var(--primary)'} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="-mt-20 text-center text-xl font-extrabold text-on-surface">{formatPoints(points.totalPoints)}</div>
            </div>
            <div className="flex-1 space-y-3">
              {pieRows.map((item) => (
                <div
                  key={item.label}
                  role="button"
                  tabIndex={0}
                  onClick={() => actions.openDetails(`${item.label} Priority Tasks`, 'task', null, null, { priority: item.label })}
                  onKeyDown={(e) => e.key === 'Enter' && actions.openDetails(`${item.label} Priority Tasks`, 'task', null, null, { priority: item.label })}
                  className="cursor-pointer rounded p-1 transition-colors hover:bg-surface-container-low"
                  title="Click to view contributing tasks"
                >
                  <div className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-1.5 font-medium text-on-surface-variant">
                      <span className="h-2 w-2 rounded-full" style={{ background: POINT_COLORS[item.label] || 'var(--primary)' }} />
                      <span className="font-bold text-on-surface">{item.label}</span>
                      <span className="text-[10px] text-on-surface-variant">({item.taskCount} tasks)</span>
                    </span>
                    <span className="font-bold font-mono">{formatPercent(item.percent)}</span>
                  </div>
                  <p className="text-[10px] text-on-surface-variant truncate">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* High SLA Risk List */}
        <div className="relative overflow-hidden rounded-xl bg-surface-container-low p-6 md:col-span-12">
          <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <h3 className="text-xs font-bold uppercase tracking-widest text-outline">High SLA Risk List</h3>
                <span className="rounded-full bg-error/10 px-2.5 py-0.5 text-xs font-bold text-error">
                  {urgentEscalations} Urgent Escalations Required
                </span>
              </div>
              <p className="mt-1 text-xs text-on-surface-variant">
                Classified by breach window: Healthy | At Risk | Critical | Breached. Click any row to view SLA details.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="rounded-lg border border-outline-variant/20 bg-white px-3.5 py-2 text-xs font-bold text-primary shadow-sm hover:bg-slate-50"
                onClick={() => actions.openSlaRisks()}
              >
                View SLA Register
              </button>
              <button
                type="button"
                className="rounded-lg bg-primary px-4 py-2 text-xs font-bold text-white shadow-sm transition-opacity hover:opacity-95 disabled:opacity-60"
                onClick={actions.exportRisk}
                disabled={state.isExporting}
              >
                {state.isExporting ? 'Exporting...' : 'Export Log'}
              </button>
            </div>
          </div>

          <div className="space-y-3">
            {state.isLoadingRisk ? (
              <div className="rounded-lg border border-outline-variant/20 bg-white p-6 text-sm text-on-surface-variant">Loading risk list...</div>
            ) : null}
            {!state.isLoadingRisk && riskRows.length
              ? riskRows.map((row) => (
                  <div
                    key={`${row.taskId}-${row.taskCode}`}
                    onClick={() => actions.openSlaRisks()}
                    className="flex cursor-pointer items-center justify-between rounded-lg border border-outline-variant/20 bg-white p-4 shadow-sm transition-transform hover:scale-[1.005]"
                  >
                    <div className="flex items-center gap-4">
                      <div
                        className={`flex h-10 w-10 items-center justify-center rounded-lg ${
                          row.slaStatus === 'Breached' || row.slaStatus === 'Critical'
                            ? 'bg-error-container text-error'
                            : row.slaStatus === 'At Risk'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-emerald-100 text-emerald-700'
                        }`}
                      >
                        <span className="material-symbols-outlined">
                          {row.slaStatus === 'Breached' ? 'priority_high' : row.slaStatus === 'Critical' ? 'warning' : 'schedule'}
                        </span>
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-on-surface">{row.taskName || row.taskCode || 'Task'}</p>
                          <span className={`rounded-full px-2 py-0.5 text-[10px] font-extrabold ${
                            row.slaStatus === 'Breached'
                              ? 'bg-rose-100 text-rose-700'
                              : row.slaStatus === 'Critical'
                                ? 'bg-orange-100 text-orange-700'
                                : row.slaStatus === 'At Risk'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-emerald-100 text-emerald-700'
                          }`}>
                            {row.slaStatus}
                          </span>
                        </div>
                        <p className="text-[10px] font-medium text-on-surface-variant">
                          Project: {row.projectName || '-'} • Dept: {row.departmentName || '-'} • Priority: {row.priority || '-'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-8">
                      <div className="hidden lg:block text-right">
                        <p className="mb-0.5 text-[9px] font-bold uppercase text-outline">Time to Breach</p>
                        <p className={`text-sm font-black ${row.slaStatus === 'Breached' || row.slaStatus === 'Critical' ? 'text-error' : 'text-on-surface'}`}>
                          {row.timeToBreachLabel || '-'}
                        </p>
                      </div>
                      <div className="hidden md:block">
                        <p className="mb-0.5 text-[9px] font-bold uppercase text-outline">Assignee</p>
                        <div className="flex -space-x-2">
                          {(row.assignees || []).slice(0, 3).map((person) =>
                            person.profilePhotoPath ? (
                              <img
                                key={`risk-person-${person.id}`}
                                src={resolveMediaUrl(person.profilePhotoPath)}
                                alt={person.name}
                                className="h-6 w-6 rounded-full border-2 border-white object-cover"
                              />
                            ) : (
                              <span
                                key={`risk-person-${person.id}`}
                                className="flex h-6 w-6 items-center justify-center rounded-full border-2 border-white bg-slate-300 text-[9px] font-bold"
                              >
                                {initials(person.name)}
                              </span>
                            ),
                          )}
                        </div>
                      </div>
                      <span className="material-symbols-outlined text-slate-400">chevron_right</span>
                    </div>
                  </div>
                ))
              : null}

            {!state.isLoadingRisk && !riskRows.length ? (
              <div className="rounded-lg border border-outline-variant/20 bg-white p-6 text-sm text-on-surface-variant">
                No ticket task SLA risks found for selected filters.
              </div>
            ) : null}
          </div>

          {riskRows.length ? (
            <AppPagination
              className="mt-6"
              page={Number(state.riskPage || 1)}
              totalPages={riskTotalPages}
              canPrevious={Number(state.riskPage || 1) > 1}
              canNext={Number(state.riskPage || 1) < riskTotalPages}
              onPrevious={() => actions.setRiskPage(Math.max(1, Number(state.riskPage || 1) - 1))}
              onNext={() => actions.setRiskPage(Math.min(riskTotalPages, Number(state.riskPage || 1) + 1))}
              onPageChange={(nextPage) => actions.setRiskPage(nextPage)}
            />
          ) : null}
        </div>
      </div>

      {/* Team Load Breakdown Modal */}
      {state.isTeamOpen ? (
        <div className="analytics-task-drawer-overlay" onClick={actions.closeTeam} role="presentation">
          <aside className="analytics-task-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-task-drawer-header">
              <div>
                <h3 className="text-3xl font-bold text-primary">Team Load Breakdown</h3>
                <p className="text-sm text-on-surface-variant">Assignee split, overdue load, and in-progress work.</p>
              </div>
              <button type="button" className="material-symbols-outlined text-on-surface-variant hover:text-on-surface" onClick={actions.closeTeam}>
                close
              </button>
            </div>
            {state.isLoadingTeam ? <p className="analytics-task-status m-4">Loading team breakdown...</p> : null}
            {state.teamError ? <p className="analytics-task-status analytics-task-status-error m-4">{state.teamError}</p> : null}
            {!state.isLoadingTeam && !state.teamError ? (
              <div className="analytics-task-drawer-content">
                <div className="analytics-task-table-wrap">
                  <table>
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Task Count</th>
                        <th>Open</th>
                        <th>In Progress</th>
                        <th>Overdue</th>
                        <th>Completed</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(state.teamLoad?.teamBreakdown?.assigneeSplit || []).length ? (
                        state.teamLoad.teamBreakdown.assigneeSplit.map((item) => (
                          <tr
                            key={`team-row-${item.userId}`}
                            onClick={() => {
                              actions.closeTeam()
                              actions.openUsers(null, null, item.userId)
                            }}
                            className="cursor-pointer hover:bg-surface-container-low"
                          >
                            <td className="font-bold text-primary hover:underline">{item.name}</td>
                            <td>{Number(item.taskCount || 0)}</td>
                            <td>{Number(item.openCount || 0)}</td>
                            <td>{Number(item.inProgressCount || 0)}</td>
                            <td className="font-bold text-error">{Number(item.overdueCount || 0)}</td>
                            <td className="font-bold text-emerald-600">{Number(item.completedCount || 0)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={6} className="analytics-task-empty-cell">
                            No team data available.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </aside>
        </div>
      ) : null}

      {/* Interactive Multi-Level Drilldown Drawer */}
      {state.isDetailsOpen ? (
        <div className="analytics-task-drawer-overlay" onClick={actions.closeDetails} role="presentation">
          <aside className="analytics-task-drawer-panel" onClick={(event) => event.stopPropagation()}>
            <div className="analytics-task-drawer-header">
              <div>
                <div className="flex items-center gap-3">
                  <h3 className="font-headline text-2xl font-bold text-primary">{state.drillTitle}</h3>
                  {state.drillPeriodFrom && state.drillPeriodTo ? (
                    <span className="rounded-full bg-primary/10 px-2.5 py-0.5 text-xs font-bold text-primary">
                      Period: {state.drillPeriodFrom} → {state.drillPeriodTo}
                    </span>
                  ) : null}
                  {state.drillStatus ? (
                    <span className="rounded-full bg-slate-100 px-2.5 py-0.5 text-xs font-bold text-on-surface">
                      Status: {state.drillStatus}
                    </span>
                  ) : null}
                </div>
                <p className="mt-1 text-sm text-on-surface-variant">
                  Tracing operational delivery across tasks, projects, contributors, delay trends, and SLA tickets.
                </p>
              </div>
              <button
                type="button"
                className="material-symbols-outlined text-on-surface-variant hover:text-on-surface"
                onClick={actions.closeDetails}
              >
                close
              </button>
            </div>
            <div className="analytics-task-drawer-content">
              <div className="mb-3 flex flex-wrap items-center gap-2 text-xs font-semibold text-on-surface-variant">
                <span>Task Analytics</span><span>/</span><span className="text-primary">{state.drillTitle}</span>
                <button type="button" className="ml-auto rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary" onClick={actions.closeDetails}>Back</button>
                <button
                  type="button"
                  className="rounded border border-outline-variant/30 px-2.5 py-1.5 text-primary"
                  onClick={() => {
                    if (state.drillLevel === 'project') actions.closeDetails()
                    else if (state.drillLevel === 'user') actions.openProjects(state.drillPeriodFrom, state.drillPeriodTo)
                    else actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)
                  }}
                >
                  Up One Level
                </button>
              </div>
              {/* Navigation Tabs with Active Underline and Right-Aligned Solid Export CSV */}
              <div className="analytics-task-drawer-nav">
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'task' ? 'active' : ''}`}
                  onClick={() => actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Task Activity
                </button>
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'project' ? 'active' : ''}`}
                  onClick={() => actions.openProjects(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Projects
                </button>
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'user' ? 'active' : ''}`}
                  onClick={() => actions.openUsers(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Assignees
                </button>
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'delay' ? 'active' : ''}`}
                  onClick={() => actions.openDelayTrend(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Delay History
                </button>
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'sla' ? 'active' : ''}`}
                  onClick={() => actions.openSlaRisks(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  SLA Risks
                </button>
                <button
                  type="button"
                  className={`analytics-task-drawer-tab ${state.drillLevel === 'history' ? 'active' : ''}`}
                  onClick={() => actions.openHistory(state.drillPeriodFrom, state.drillPeriodTo)}
                >
                  Status / Activity History
                </button>
                <button
                  type="button"
                  className="analytics-task-drawer-tab"
                  onClick={actions.resetFilters}
                >
                  Reset Drilldown
                </button>
                <button
                  type="button"
                  className="ml-auto rounded bg-primary px-3 py-2 text-xs font-bold text-white transition-opacity hover:opacity-95 disabled:opacity-60"
                  onClick={() => actions.exportCurrentDrilldown('csv')}
                  disabled={state.isExporting}
                >
                  {state.isExporting ? 'Exporting...' : 'Export CSV'}
                </button>
                <button type="button" className="rounded border border-primary px-3 py-2 text-xs font-bold text-primary disabled:opacity-60" onClick={() => actions.exportCurrentDrilldown('pdf')} disabled={state.isExporting}>Export PDF</button>
              </div>

              {state.drilldownError ? <p className="mb-4 text-sm text-error">{state.drilldownError}</p> : null}
              {state.isLoadingDrilldown ? <p className="mb-4 text-sm text-on-surface-variant">Loading drill-down data...</p> : null}

              <AnalyticsTaskTable
                className="analytics-task-list-card--drawer"
                columns={[
                  ...drillColumns.map(titleCase),
                  ...(['task', 'project', 'user', 'delay', 'sla', 'history'].includes(state.drillLevel) ? ['Action'] : [])
                ]}
                rows={drillRows}
                rowKey={(row, idx) => `${state.drillLevel}-${row.id || row.taskCode || row.projectCode || row.userId || idx}`}
                emptyMessage={state.isLoadingDrilldown ? 'Loading details...' : 'No drill-down records match the current filters.'}
                renderRow={(row) => (
                  <>
                    {drillColumns.map((key) => {
                      const value = row[key]
                      if (key === 'status' || key === 'priority' || key === 'slaStatus' || key === 'delayStatus') {
                        const isBreached = value === 'Breached' || value === 'OVERDUE' || value === 'Active Overdue'
                        return (
                          <td key={key}>
                            <span className={`analytics-task-list-badge ${isBreached ? 'analytics-task-list-count--alert' : ''}`}>
                              {value || '-'}
                            </span>
                          </td>
                        )
                      }
                      const isPercent = key === 'completionPercent'
                      const isDays = key === 'delayDays'
                      let formatted = value ?? '-'
                      if (typeof value === 'number') {
                        if (isPercent) formatted = `${value.toFixed(1)}%`
                        else if (isDays) formatted = `${value}d`
                        else formatted = value.toLocaleString('en-IN')
                      }
                      const isPrimary = ['taskCode', 'taskName', 'projectCode', 'projectName', 'userName'].includes(key)
                      const isNumeric = typeof value === 'number' || isPercent || isDays
                      const isDelay = key === 'delayDays' && Number(value || 0) > 0
                      return (
                        <td
                          key={key}
                          className={`${isPrimary ? 'analytics-task-list-primary' : ''} ${isNumeric ? 'analytics-task-list-number' : ''} ${isDelay ? 'analytics-task-list-negative' : ''}`}
                        >
                          {formatted}
                        </td>
                      )
                    })}
                    {state.drillLevel === 'task' && row.id ? (
                      <td>
                        <button
                          type="button"
                          className="analytics-task-list-action"
                          onClick={() => navigate(`/nerve/task/entry?id=${row.id}`)}
                        >
                          View Task <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                    {state.drillLevel === 'project' && row.id ? (
                      <td>
                        <button
                          type="button"
                          className="analytics-task-list-action"
                          onClick={() => actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)}
                        >
                          View Tasks <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                    {state.drillLevel === 'user' && row.id ? (
                      <td>
                        <button
                          type="button"
                          className="analytics-task-list-action"
                          onClick={() => actions.openTasks(state.drillPeriodFrom, state.drillPeriodTo)}
                        >
                          View Tasks <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                    {['delay', 'sla', 'history'].includes(state.drillLevel) && (row.taskId || row.id) ? (
                      <td>
                        <button type="button" className="analytics-task-list-action" onClick={() => navigate(`/nerve/task/entry?id=${row.taskId || row.id}`)}>
                          View Task <span className="material-symbols-outlined">arrow_forward</span>
                        </button>
                      </td>
                    ) : null}
                  </>
                )}
              />
            </div>
          </aside>
        </div>
      ) : null}
    </div>
  )
}
